import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormHelperText,
  Skeleton,
  TextField,
} from '@mui/material'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { useContext, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import ReactMarkdown from 'react-markdown'
import { useFieldOptions } from '../../../hooks/data/useFieldOptions'
import { useRefData } from '../../../hooks/data/useRefData'
import { setDirtyForm } from '../../../services/appPreferences/dirtyFormStorage'
import AppContext from '../../../context/appContext'

const checkedIcon = <CheckBoxIcon fontSize="small" />
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />

const AutocompleteWrapper = ({
  control,
  formField,
  rules,
  readOnly,
  fieldArrayName,
  fieldArrayIndex,
}) => {
  const { watch } = useFormContext()
  const { setPartialChanges, currentFarm } = useContext(AppContext)

  const { optionsFromField } = formField
  const sourceValues = optionsFromField?.sourceField ? watch(optionsFromField.sourceField) : null
  const filterValue = formField.filterBy
    ? fieldArrayName
      ? watch(`${fieldArrayName}.${fieldArrayIndex}.${formField.filterBy}`)
      : watch(formField.filterBy)
    : null

  const hasDefaultOptions = Boolean(formField.defaultOptions?.length)
  const refData = useRefData(
    optionsFromField || hasDefaultOptions ? undefined : formField.refDataKey,
  )

  const fieldOptions = useFieldOptions({ optionsFromField, sourceValues, farmId: currentFarm?.id })

  const options = useMemo(() => {
    if (optionsFromField) return fieldOptions.data
    if (hasDefaultOptions) return formField.defaultOptions
    if (!refData.data) return undefined
    return formField.filterBy
      ? refData.data.filter(option => option[formField.filterBy] === filterValue)
      : refData.data
  }, [
    optionsFromField,
    fieldOptions.data,
    hasDefaultOptions,
    formField.defaultOptions,
    formField.filterBy,
    refData.data,
    filterValue,
  ])

  const isLoading = fieldOptions.isLoading || refData.isLoading

  if (isLoading) {
    return (
      <Skeleton
        height={56}
        sx={{
          borderRadius: 1,
          maxWidth: formField.maxWidth,
        }}
        variant="rectangular"
        width="100%"
      />
    )
  }

  const getOptionLabel = option => {
    if (typeof option === 'string') return option
    return option.es_AR ?? option.label ?? option.name ?? ''
  }

  const isOptionEqualToValue = (option, value) => {
    if (typeof option === 'string' && typeof value === 'string') {
      return option === value
    }
    return option.id === value.id || option.value === value.value
  }

  const renderOption = (props, option, { selected }) => {
    const { key, ...rest } = props
    if (formField.multiple && formField.checkboxes) {
      return (
        <li key={`${formField.name}-ac-${option.id || option.value}`} {...rest}>
          <Checkbox
            checked={selected}
            checkedIcon={checkedIcon}
            icon={icon}
            size="small"
            style={{ marginRight: 8 }}
          />
          {getOptionLabel(option)}
        </li>
      )
    }
    return (
      <li key={key} {...rest}>
        {getOptionLabel(option)}
      </li>
    )
  }

  return (
    options && (
      <Controller
        control={control}
        defaultValue={formField.multiple ? [] : formField.defaultValue}
        name={formField.name}
        render={({ field, fieldState }) => (
          <FormControl disabled={readOnly} sx={{ width: '100%' }} variant="outlined">
            <Autocomplete
              autoComplete={formField.autoComplete}
              autoHighlight={formField.autoHighlight}
              autoSelect={formField.autoSelect}
              blurOnSelect={formField.blurOnSelect}
              clearOnBlur={formField.clearOnBlur}
              clearOnEscape={formField.clearOnEscape}
              disableClearable={formField.disableClearable}
              disableCloseOnSelect={formField.multiple && formField.checkboxes}
              disabled={readOnly}
              disableListWrap={formField.disableListWrap}
              filterOptions={(optionsToFilter, { inputValue }) => {
                if (formField.customFilter) {
                  return formField.customFilter(optionsToFilter, { inputValue })
                }
                return optionsToFilter.filter(option =>
                  getOptionLabel(option).toLowerCase().includes(inputValue.toLowerCase()),
                )
              }}
              flat={formField.flat}
              freeSolo={formField.freeSolo}
              getOptionLabel={getOptionLabel}
              includeInputInList={formField.includeInputInList}
              isOptionEqualToValue={isOptionEqualToValue}
              limitTags={formField.limitTags}
              multiple={formField.multiple}
              openOnFocus={formField.openOnFocus}
              options={options}
              renderInput={params => (
                <TextField
                  {...params}
                  error={!!fieldState.error}
                  fullWidth={formField.fullWidth}
                  label={formField.placeholder}
                  placeholder={formField.placeholder}
                  sx={{
                    maxWidth: formField.maxWidth,
                  }}
                />
              )}
              renderOption={renderOption}
              selectOnFocus={formField.selectOnFocus}
              size={formField.size || 'medium'}
              value={field.value}
              onChange={(event, newValue) => {
                setPartialChanges(true)
                field.onChange(newValue)
                if (!readOnly) setDirtyForm(true)
              }}
              onInputChange={(event, newInputValue) => {
                if (formField.onInputChange) {
                  formField.onInputChange(event, newInputValue)
                }
              }}
            />
            <FormHelperText error={fieldState.error}>
              <ReactMarkdown>
                {fieldState.error ? fieldState.error.message : formField.helperText}
              </ReactMarkdown>
            </FormHelperText>
          </FormControl>
        )}
        rules={rules}
      />
    )
  )
}

export default AutocompleteWrapper
